import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { toast } from "sonner";
import HintButton from "@/components/HintButton";
import PushNotifyCard from "@/components/PushNotifyCard";
import RestoreModal from "@/components/backup/RestoreModal";
import BackupStatsGrid from "@/components/backup/BackupStatsGrid";
import PredeploySection from "@/components/backup/PredeploySection";
import BackupConfigSection from "@/components/backup/BackupConfigSection";
import BackupHistoryTable from "@/components/backup/BackupHistoryTable";
import BackupChoiceModal from "@/components/backup/BackupChoiceModal";
import { DESTINATION_LABEL } from "@/components/backup/backupUtils";
import { CloudArrowUp, ArrowsClockwise, Play, ArrowCounterClockwise } from "@phosphor-icons/react";

export default function Backup() {
  const { canAdmin, ready: permsReady } = useCanAdmin();
  // "isAdmin" é o gate de leitura (config/histórico). Ações destrutivas usam
  // `canAdmin`, que também aceita permissão explícita `admin_actions`.
  const isAdmin = canAdmin;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runDest, setRunDest] = useState(null);
  const [showChoice, setShowChoice] = useState(false);

  const [config, setConfig] = useState(null);
  const [cfgDraft, setCfgDraft] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [predeployList, setPredeployList] = useState([]);
  const [predeployRunning, setPredeployRunning] = useState(false);

  // Restore state (só controle de abertura — o resto vive dentro do modal)
  const [showRestore, setShowRestore] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/history", { params: { limit: 100 } });
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar histórico");
    } finally { setLoading(false); }
  };

  const loadConfig = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/backup/config");
      setConfig(data);
      setCfgDraft({
        auto_enabled: !!data.auto_enabled,
        schedule_time: data.schedule_time || "03:00",
        drive_folder_id: data.drive_folder_id || "",
        drive_service_account_json: "", // vazio = "não alterar"
        email_recipients: (data.email_recipients || []).join(", "),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar configuração");
    }
  };

  const loadPredeploy = async () => {
    try {
      const { data } = await api.get("/backup/predeploy/list");
      setPredeployList(data?.files || []);
    } catch (e) {
      console.debug("[Backup] loadPredeploy falhou:", e?.response?.status || e?.message);
    }
  };

  useEffect(() => {
    // Aguarda `useCanAdmin` resolver antes de decidir se busca config
    // (evita loadConfig() rodar com isAdmin=false na 1ª renderização e nunca
    // re-fetch — bug que fazia a seção de config sumir para admin real).
    if (!permsReady) return;
    loadHistory();
    if (isAdmin) {
      loadConfig();
      loadPredeploy();
    }
    // eslint-disable-next-line
  }, [permsReady, isAdmin]);

  const runBackup = async (destination) => {
    setShowChoice(false);
    setRunning(true);
    setRunDest(destination);
    const toastId = toast.loading(
      destination === "download"
        ? "Gerando arquivo de backup..."
        : destination === "email"
          ? "Gerando backup e enviando por e-mail..."
          : "Gerando backup e enviando para o Google Drive..."
    );
    try {
      const { data } = await api.post("/backup/run", { destination });
      if (!data.ok) {
        toast.error(data.error || "Falha no backup", { id: toastId });
      } else if (destination === "download") {
        toast.success("Backup pronto! Iniciando download...", { id: toastId });
        // baixa via link autenticado (cookie HttpOnly)
        const url = `${API}/backup/download/${data.history_id}`;
        try {
          const resp = await fetch(url, { credentials: "include" });
          const blob = await resp.blob();
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = data.file_path ? data.file_path.split("/").pop() : `backup_${Date.now()}.archive.gz`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (e) {
          toast.error("Backup gerado mas download falhou. Baixe pelo histórico.");
        }
      } else {
        toast.success(
          destination === "email"
            ? `E-mail enviado para: ${data.email_sent_to || "destinatários"}`
            : `Enviado para o Drive (${data.drive_file_name})`,
          { id: toastId }
        );
      }
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao executar backup", { id: toastId });
    } finally {
      setRunning(false);
      setRunDest(null);
    }
  };

  const saveConfig = async () => {
    if (!cfgDraft) return;
    setSavingCfg(true);
    try {
      const payload = {
        auto_enabled: cfgDraft.auto_enabled,
        schedule_time: cfgDraft.schedule_time || "03:00",
        drive_folder_id: cfgDraft.drive_folder_id || "",
        email_recipients: (cfgDraft.email_recipients || "")
          .split(/[,;\n]/).map((s) => s.trim()).filter(Boolean),
      };
      // Só envia o JSON se admin escreveu algo novo
      if (cfgDraft.drive_service_account_json && cfgDraft.drive_service_account_json.trim().length > 10) {
        payload.drive_service_account_json = cfgDraft.drive_service_account_json.trim();
      }
      const { data } = await api.put("/backup/config", payload);
      setConfig(data);
      setCfgDraft((d) => ({ ...d, drive_service_account_json: "" }));
      toast.success("Configuração salva!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar configuração");
    } finally { setSavingCfg(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/backup/test-connection");
      setTestResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no teste");
    } finally { setTesting(false); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Remover este registro do histórico? (Se for arquivo local, será apagado)")) return;
    try {
      await api.delete(`/backup/history/${id}`);
      toast.success("Removido");
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover");
    }
  };

  const downloadEntry = async (row) => {
    try {
      const resp = await fetch(`${API}/backup/download/${row.id}`, {
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        toast.error(j.detail || "Arquivo não disponível");
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = row.file_name || `backup_${Date.now()}.archive.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error("Download falhou");
    }
  };

  const runPredeploy = async () => {
    setPredeployRunning(true);
    const toastId = toast.loading("Gerando backup de segurança...");
    try {
      const { data } = await api.post("/backup/predeploy/run");
      if (data?.ok) {
        toast.success(`Backup salvo: ${data.file_name}`, { id: toastId });
      } else {
        toast.error(data?.error || "Falha ao gerar backup", { id: toastId });
      }
      await loadPredeploy();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro", { id: toastId });
    } finally {
      setPredeployRunning(false);
    }
  };

  const downloadPredeploy = async (fileName) => {
    try {
      const resp = await fetch(`${API}/backup/predeploy/download/${encodeURIComponent(fileName)}`, {
        credentials: "include",
      });
      if (!resp.ok) {
        toast.error("Não foi possível baixar o arquivo");
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Download falhou");
    }
  };

  const totals = useMemo(() => {
    const t = { total: history.length, success: 0, failed: 0, partial: 0 };
    history.forEach((r) => {
      if (r.status === "success") t.success++;
      else if (r.status === "failed") t.failed++;
      else if (r.status === "partial") t.partial++;
    });
    return t;
  }, [history]);

  return (
    <div className="p-6 md:p-10" data-testid="backup-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500 mb-2">SISTEMA</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Backup do Banco</h1>
          <HintButton text="Cópia de segurança completa do banco de dados (pedidos, estoque, produção, cadastros, financeiro, logs). Envio manual ou automático diário para Google Drive e E-mail." />
        </div>
        <div className="flex items-center gap-2">
          <button className="btn btn-ghost" onClick={loadHistory} data-testid="backup-refresh">
            <ArrowsClockwise size={16} weight="bold" /> Atualizar
          </button>
          {isAdmin && (
            <button
              className="btn btn-ghost !border-[#E60000] !text-[#E60000] hover:!bg-[#E60000] hover:!text-white"
              onClick={() => setShowRestore(true)}
              disabled={running}
              data-testid="backup-restore-btn"
              title="Restaurar banco a partir de um arquivo .archive.gz"
            >
              <ArrowCounterClockwise size={16} weight="bold" /> Restaurar Backup
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => setShowChoice(true)}
            disabled={running}
            data-testid="backup-run-btn"
          >
            <Play size={16} weight="fill" />
            {running ? "Executando..." : "Fazer Backup Agora"}
          </button>
        </div>
      </div>

      <BackupStatsGrid totals={totals} />

      {/* Notificações Push (Alertas de pedidos atrasados) */}
      {isAdmin && (
        <div className="mb-8">
          <PushNotifyCard />
        </div>
      )}

      {/* Backup Pré-Deploy — proteção automática contra perda em deploys */}
      {isAdmin && (
        <PredeploySection
          files={predeployList}
          running={predeployRunning}
          onRun={runPredeploy}
          onDownload={downloadPredeploy}
        />
      )}

      {/* Config (só admin) */}
      {isAdmin && cfgDraft && (
        <BackupConfigSection
          config={config}
          cfgDraft={cfgDraft}
          setCfgDraft={setCfgDraft}
          savingCfg={savingCfg}
          testing={testing}
          testResult={testResult}
          onSave={saveConfig}
          onTest={testConnection}
        />
      )}

      <BackupHistoryTable
        history={history}
        loading={loading}
        isAdmin={isAdmin}
        onDownload={downloadEntry}
        onDelete={deleteEntry}
      />

      {/* Modal escolha do destino */}
      {showChoice && (
        <BackupChoiceModal config={config} onRun={runBackup} onClose={() => setShowChoice(false)} />
      )}

      {/* Overlay quando rodando */}
      {running && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center pointer-events-none">
          <div className="bg-white border border-black px-6 py-4 flex items-center gap-3">
            <CloudArrowUp size={24} weight="bold" className="animate-pulse" />
            <div>
              <div className="font-bold">Executando backup...</div>
              <div className="text-xs text-zinc-500">Destino: {DESTINATION_LABEL[runDest] || runDest}</div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de RESTORE — auto-contido (state + logic no componente) */}
      <RestoreModal
        open={showRestore}
        onClose={() => setShowRestore(false)}
        driveConfigured={!!config?.drive_configured}
        onRestored={loadHistory}
      />
    </div>
  );
}
